import Configuration from '@/agilis/util/Configuration'
import DataTableV2ImportResult from '@/agilis/components/DataTableV2/DataTableV2ImportReader/DataTableV2ImportResult.vue'
import FormMixin from '@/agilis/components/Form/utils/FormMixin'
import ValidationMixin from '@/agilis/components/Form/utils/validationMixin'
import L from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import readXlsxFile from 'read-excel-file'
import { validate } from 'vee-validate'
import jsonLogic from '@/plugins/jsonLogic'
import ErrorUtils from '@/agilis/util/Error'
import { parseDateString } from '@/plugins/moment'

const RESULT_CONFIGURATION = {
  itemDecoration: {
    backgroundColor: {
      jsonLogic: {
        if: [
          { '==': [{ var: 'importResult' }, 'OK'] }, 'transparent',
          { '==': [{ var: 'importResult' }, 'KO'] }, 'deep-orange lighten-5',
          { '==': [{ var: 'importResult' }, 'WARN'] }, 'yellow lighten-5',
          'transparent'
        ]
      }
    }
  },
  columns: {
    excelRow: {
      id: 'excelRow',
      label: 'label.blank',
      type: 'text',
      header: true
    },
    id: {
      header: false
    },
    actions: {
      header: false
    },
    importResult: {
      id: 'importResult',
      type: 'text',
      header: false
    },
    importMessage: {
      id: 'importMessage',
      label: 'label.result',
      type: 'text',
      header: true
    }
  },
  columnsOrder: [
    'excelRow'
  ]
}

export default {
  name: 'FileImportReader',
  mixins: [FormMixin, ValidationMixin],

  props: {
    dataTableConfiguration: Object
  },

  components: {
    DataTableV2ImportResult
  },

  data () {
    return {
      xlsxImporting: false,
      keyFileImport: 1000,
      resultDialog: false,
      items: [],
      headers: []
    }
  },

  computed: {
    ...mapGetters({
      userId: 'userDataStore/userId',
      selectedContext: 'contextStore/selectedContext',
      applicationId: 'contextStore/applicationId',
      workspaceId: 'contextStore/workspaceId',
      trackerTypeId: 'contextStore/trackerTypeId',
      loaders: 'loaderStore/loading'
    }),

    resultTitle () {
      return this.$t('label.importResults')
    },

    resultConfiguration () {
      return L.merge({}, this.dataTableConfiguration, RESULT_CONFIGURATION)
    },
    importing () {
      return this.loaders['import-loader']
    }
  },

  methods: {
    ...mapActions({
      'notifyError': 'notificationStore/notifyError',
      'notifyUserConfirm': 'notificationStore/notifyUserConfirm',
      startProgress: 'loaderStore/startProgress',
      stopProgress: 'loaderStore/stopProgress'
    }),

    async onFileImportPicked (event) {
      try {
        this.startProgress({ id: 'import-loader' })
        this.resultDialog = true
        this.items = []
        const files = event.target.files

        this.xlsxImporting = true
        for (var i = 0; i < files.length; i++) {
          await this.saveRows(files[i])
          this.keyFileImport += 1
        }
        this.xlsxImporting = false
        if (this.items.length > 0) {
          this.resultDialog = true
          this.$emit('file-imported')
        }
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.stopProgress({ id: 'import-loader' })
      }
    },

    async saveRows (file) {
      try {
        await readXlsxFile(file).then(this.importRows)
      } catch (error) {
        const message = file.name + ': ' + error.message
        this.notifyError({ message })
      }
    },

    async importRows (rows) {
      this.headers = rows[0]
      if (this.headers && this.headers.length > 0) {
        for (var rowIndex = 1; rowIndex < rows.length; rowIndex++) {
          const row = rows[rowIndex]
          let item = {
            applicationId: this.applicationId,
            workspaceId: this.workspaceId,
            trackerTypeId: this.trackerTypeId
          }
          for (var headerIndex = 0; headerIndex < this.headers.length; headerIndex++) {
            const header = this.headers[headerIndex]
            const value = row[headerIndex]
            if (value !== null) {
              L.set(item, header, value)
            }
          }

          let columnErrors = []
          const columns = L.cloneDeep(this.dataTableConfiguration.columns)
          for (const colConfig of Object.values(columns)) {
            const computedFormField = this.computedFormField(colConfig.id, colConfig, { formModel: item })

            if (item[computedFormField.id] && computedFormField.type === 'select' && computedFormField.options) {
              const id = Configuration.datatable.searchInOptions(item[computedFormField.id], 'EQUALS', computedFormField.options, {})
              if (!Array.isArray(id)) {
                item[computedFormField.id] = id ?? item[computedFormField.id]
              }
            }

            if (item[computedFormField.id] && computedFormField.propagateTo) {
              Configuration.datatable.propagateFieldInheritance(item, item, computedFormField)
            }

            const importConfig = computedFormField.import

            if (importConfig?.dataCleaning && importConfig?.dataCleaning.jsonLogic) {
              const cleaningOperations = importConfig.dataCleaning.jsonLogic
              item[computedFormField.id] = jsonLogic.apply(cleaningOperations, item)
            }

            if (item[computedFormField.id] && computedFormField.type === 'date') {
              if (!L.isDate(item[computedFormField.id])) {
                const parsedDate = parseDateString(item[computedFormField.id])
                if (!parsedDate) {
                  columnErrors.push(this.$t('label.formInvalidFieldValidation', { field: this.$t(computedFormField.label) || computedFormField.id }))
                }
              }
            }

            let rules = computedFormField.rules
            if (computedFormField.required) {
              rules = rules || {}
              rules.required = true
            }
            if (rules) {
              const validations = Object.entries(rules).map(async ([key, value]) => {
                const ruleName = { [key]: value }
                const result = await validate(item[computedFormField.id], ruleName, { name: this.$t(computedFormField.label) || computedFormField.id })
                if (!result.valid) {
                  const errors = this.errorMessage(result.failedRules, result.errors, computedFormField.messages)
                  const error = errors[0]
                  if (!columnErrors.includes(error)) {
                    columnErrors.push(error)
                  }
                }
              })

              await Promise.all(validations)
            }
          }

          if (columnErrors.length > 0) {
            const importError = columnErrors.join(', \n')
            this.addItemToResultSet(item, rowIndex, importError)
            continue
          }

          const api = this.$axios.agilisUrlTracker(
            this.applicationId,
            this.trackerTypeId
          )
          const url = `${api}/import`
          await this.$axios.post(url, item, this.$axios.agilisRequestConfig(url))
            .then(response => {
              this.addItemToResultSet(response.data, rowIndex)
            })
            .catch(error => {
              this.addItemToResultSet(item, rowIndex, error)
            })
        }
      }
    },
    addItemToResultSet (item, rowNumber, importError) {
      item.excelRow = rowNumber + 1
      if (importError) {
        item.importMessage = ErrorUtils.getErrorMessage(importError, item.trackerTypeId, this.applicationId)?.message
        item.importResult = 'KO'
        for (const colConfig of Object.values(this.dataTableConfiguration.columns)) {
          if (colConfig.type === 'select' && item[colConfig.id] && typeof item[colConfig.id] === 'object') {
            item[colConfig.id] = null
          }
        }
      } else {
        item.importMessage = this.$t('message.importSuccess')
        item.importResult = 'OK'
      }
      this.items.push(item)
    },

    chooseFileImport () {
      this.$refs.fileImport.click()
    },

    closeResultDialog () {
      this.resultDialog = false
    }
  }
}
